import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PieChart, Pie, Cell, Legend, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';

const COLORS_BOOKED = '#22c55e';
const COLORS_FREE = '#ef4444';
const YEAR_BAR_COLORS = [
  '#3b82f6', '#f97316', '#8b5cf6', '#ec4899', '#14b8a6',
  '#eab308', '#6366f1', '#f43f5e', '#06b6d4', '#84cc16',
];

const daysInMonth = (year, month) => new Date(year, month, 0).getDate();

const getBookedDaysInMonth = (bookings, year, month) => {
  const monthStart = new Date(year, month - 1, 1);
  const monthEnd = new Date(year, month, 0);
  const bookedDays = new Set();

  bookings.forEach((b) => {
    const bStart = new Date(b.start_date);
    const bEnd = new Date(b.end_date);
    if (bEnd < monthStart || bStart > monthEnd) return;

    const overlapStart = bStart < monthStart ? monthStart : bStart;
    const overlapEnd = bEnd > monthEnd ? monthEnd : bEnd;

    for (
      let d = new Date(overlapStart);
      d <= overlapEnd;
      d.setDate(d.getDate() + 1)
    ) {
      bookedDays.add(d.getDate());
    }
  });

  return bookedDays.size;
};

const MONTH_KEYS = [
  'month_jan', 'month_feb', 'month_mar', 'month_apr',
  'month_may', 'month_jun', 'month_jul', 'month_aug',
  'month_sep', 'month_oct', 'month_nov', 'month_dec',
];

const OccupancyTab = ({ bookings }) => {
  const { t } = useTranslation();
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  // View mode: 'monthly' or 'yearly'
  const [viewMode, setViewMode] = useState('monthly');

  // Monthly view state
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);

  // Yearly comparison state
  const [comparisonYears, setComparisonYears] = useState([currentYear]);

  // Derive available years from bookings
  const availableYears = useMemo(() => {
    const years = new Set();
    bookings.forEach((b) => {
      years.add(new Date(b.start_date).getFullYear());
      years.add(new Date(b.end_date).getFullYear());
    });
    years.add(currentYear);
    return Array.from(years).sort((a, b) => b - a);
  }, [bookings, currentYear]);

  // Build year-month options
  const yearMonthOptions = useMemo(() => {
    const options = [];
    availableYears.forEach((y) => {
      const maxMonth = y === currentYear + 1 ? 12 : 12;
      for (let m = 1; m <= maxMonth; m++) {
        options.push({ year: y, month: m });
      }
    });
    options.sort((a, b) => b.year - a.year || b.month - a.month);
    return options;
  }, [availableYears, currentYear]);

  // Monthly pie data
  const monthlyData = useMemo(() => {
    const total = daysInMonth(selectedYear, selectedMonth);
    const booked = getBookedDaysInMonth(bookings, selectedYear, selectedMonth);
    const free = total - booked;
    const rate = total > 0 ? ((booked / total) * 100).toFixed(1) : 0;
    return { total, booked, free, rate };
  }, [bookings, selectedYear, selectedMonth]);

  const pieData = [
    { name: t('occ_booked_days'), value: monthlyData.booked },
    { name: t('occ_free_days'), value: monthlyData.free },
  ];

  // Yearly bar data
  const yearlyBarData = useMemo(() => {
    const data = [];
    for (let m = 1; m <= 12; m++) {
      const entry = { month: t(MONTH_KEYS[m - 1]) };
      comparisonYears.forEach((y) => {
        const total = daysInMonth(y, m);
        const booked = getBookedDaysInMonth(bookings, y, m);
        entry[String(y)] = total > 0 ? parseFloat(((booked / total) * 100).toFixed(1)) : 0;
      });
      data.push(entry);
    }
    return data;
  }, [bookings, comparisonYears, t]);

  const handleYearToggle = (year) => {
    setComparisonYears((prev) =>
      prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year]
    );
  };

  const handleMonthSelect = (e) => {
    const [y, m] = e.target.value.split('-').map(Number);
    setSelectedYear(y);
    setSelectedMonth(m);
  };

  return (
    <div className="w-full">
      <h2 className="text-2xl font-bold mb-6">{t('occupancy_rates')}</h2>

      {/* View Mode Toggle */}
      <div className="flex gap-4 mb-6">
        <button
          onClick={() => setViewMode('monthly')}
          className={`px-4 py-2 rounded font-medium text-sm ${
            viewMode === 'monthly'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          {t('occ_monthly_view')}
        </button>
        <button
          onClick={() => setViewMode('yearly')}
          className={`px-4 py-2 rounded font-medium text-sm ${
            viewMode === 'yearly'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          {t('occ_yearly_view')}
        </button>
      </div>

      {/* MONTHLY VIEW */}
      {viewMode === 'monthly' && (
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <div className="flex items-center gap-4 mb-6">
            <label className="font-medium text-gray-700">{t('occ_select_month')}:</label>
            <select
              value={`${selectedYear}-${selectedMonth}`}
              onChange={handleMonthSelect}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            >
              {yearMonthOptions.map(({ year, month }) => (
                <option key={`${year}-${month}`} value={`${year}-${month}`}>
                  {t(MONTH_KEYS[month - 1])} {year}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col md:flex-row items-center gap-8">
            {/* Pie Chart */}
            <div className="w-full md:w-1/2" style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    <Cell fill={COLORS_BOOKED} />
                    <Cell fill={COLORS_FREE} />
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Summary */}
            <div className="w-full md:w-1/2 space-y-3">
              <h3 className="text-lg font-semibold">
                {t(MONTH_KEYS[selectedMonth - 1])} {selectedYear}
              </h3>
              <p className="text-gray-600">
                {t('occ_total_days')}: <span className="font-bold">{monthlyData.total}</span>
              </p>
              <p className="text-green-600">
                {t('occ_booked_days')}: <span className="font-bold">{monthlyData.booked}</span>
              </p>
              <p className="text-red-600">
                {t('occ_free_days')}: <span className="font-bold">{monthlyData.free}</span>
              </p>
              <p className="text-xl font-bold text-blue-600">
                {t('occ_rate')}: {monthlyData.rate}%
              </p>
            </div>
          </div>
        </div>
      )}

      {/* YEARLY VIEW */}
      {viewMode === 'yearly' && (
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          {/* Year checkboxes */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span className="font-medium text-gray-700">{t('occ_compare_years')}:</span>
            {availableYears.map((year, idx) => (
              <label key={year} className="flex items-center gap-1 text-sm">
                <input
                  type="checkbox"
                  checked={comparisonYears.includes(year)}
                  onChange={() => handleYearToggle(year)}
                  className="accent-blue-600"
                />
                <span
                  className="font-medium"
                  style={{ color: YEAR_BAR_COLORS[idx % YEAR_BAR_COLORS.length] }}
                >
                  {year}
                </span>
              </label>
            ))}
          </div>

          {/* Bar Chart */}
          <div style={{ height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={yearlyBarData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis
                  domain={[0, 100]}
                  tickFormatter={(v) => `${v}%`}
                  label={{
                    value: t('occ_rate'),
                    angle: -90,
                    position: 'insideLeft',
                    style: { fontSize: 12 },
                  }}
                />
                <Tooltip formatter={(v) => `${v}%`} />
                <Legend />
                {comparisonYears
                  .slice()
                  .sort()
                  .map((year) => {
                    const idx = availableYears.indexOf(year);
                    return (
                      <Bar
                        key={year}
                        dataKey={String(year)}
                        name={String(year)}
                        fill={YEAR_BAR_COLORS[idx % YEAR_BAR_COLORS.length]}
                      />
                    );
                  })}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default OccupancyTab;
